// ft3d-hud/dice-window.ts — the standalone dice window.
//
// ⛔ Nothing here clones a dice control. The window is a MOUNT: the live quick roller, complex
// roller and the Mod / ADV-DIS / private-roll controls are MOVED out of #sideLog with appendChild,
// exactly as portalFt3dAudioSettings() already moves the mixer rows (my_campaign.php). A DOM move
// preserves element identity, so every getElementById reader follows the node —
// ftGetEncounterContext()'s ftContextElementValue('raw_dice_mod') funnel (template.js) and
// the retired 2-D map module included — and the delegated [data-ft-roll-die] / [data-ft-sidelog-action] handlers
// stay the one roll authority.

const DICE_OPEN_KEY = 'ft3d.hud.dice';

/** Groups tagged in includes/sidepanels/sidelog.php, in the order they mount. */
const DICE_GROUPS = ['quick', 'complex', 'controls'] as const;

/**
 * Receipt for the PORTAL OPERATION only. `portalWrites: 0` means mounting the window sent no
 * request and changed no state — it does NOT mean the controls inside are inert: rolling from them
 * inserts a roll, exactly as rolling from the legacy panel always did.
 */
interface Ft3dDiceWindowReceipt {
    schema: 'ft.ft3d.dice-window.v1';
    portalIsQueryOnly: true;
    portalWrites: 0;
    movedGroups: string[];
    /** IDs proven to still resolve after the move — the smoke asserts on these. */
    canonicalIds: string[];
}

let windowEl: HTMLElement | null = null;
let toggleEl: HTMLElement | null = null;
const listeners = new Set<(open: boolean) => void>();

function readOpen(): boolean {
    try { return window.localStorage.getItem(DICE_OPEN_KEY) === 'on'; } catch { return false; }
}

function writeOpen(open: boolean): void {
    try { window.localStorage.setItem(DICE_OPEN_KEY, open ? 'on' : 'off'); } catch { /* viewer-local nicety only */ }
}

function portalDiceControls(host: HTMLElement): string[] {
    const moved: string[] = [];
    for (const group of DICE_GROUPS) {
        const source = document.querySelector<HTMLElement>(`#sideLog [data-ft-dice-group="${group}"]`);
        const mount = host.querySelector<HTMLElement>(`[data-ft3d-dice-mount="${group}"]`);
        if (!source || !mount) continue;
        mount.appendChild(source);
        moved.push(group);
    }
    return moved;
}

function renderGlyph(): void {
    if (!toggleEl) return;
    // Rim-hosted: the bezel owns the glyph (data-ft3d-action-icon), so there is nothing to fill here.
    if (toggleEl.hasAttribute('data-ft3d-action-icon')) return;
    const glyph = toggleEl.querySelector<HTMLElement>('[data-ft3d-button-glyph]');
    const iconMapFn = (window as unknown as { iconMap?: (k: string, w: number, h: number, t: string) => string }).iconMap;
    if (!glyph || typeof iconMapFn !== 'function') return;
    glyph.innerHTML = iconMapFn('d20', 22, 22, 'Dice');
}

function apply(open: boolean): void {
    if (windowEl) windowEl.hidden = !open;
    // The dice window is a HUD overlay at z 20000, so it wins over any route window underneath.
    // This class lets the route shorten its own bottom-right windows instead of being covered.
    document.documentElement.classList.toggle('ft3d-dice-window-open', open);
    if (toggleEl) {
        toggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggleEl.classList.toggle('is-active', open);
        const bindings = window as Window & { ftGetFt3dKeyBinding?: (action: string) => string };
        const binding = bindings.ftGetFt3dKeyBinding?.('dice') ?? 'v';
        const suffix = binding ? ` (${binding.toUpperCase()})` : '';
        const label = (open ? 'Hide the dice window' : 'Show the dice window') + suffix;
        toggleEl.setAttribute('aria-keyshortcuts', binding);
        toggleEl.setAttribute('aria-label', label);
        toggleEl.setAttribute('title', label);
    }
    // Isolated: one throwing subscriber must not stop the rest from updating.
    listeners.forEach((fn) => { try { fn(open); } catch { /* presentation only */ } });
}

/**
 * Before the window has mounted there is no DOM to read, so fall back to the stored preference —
 * otherwise a control that subscribes early would render "closed" for a viewer whose window is
 * about to open.
 */
export function isDiceWindowOpen(): boolean {
    if (!windowEl) return readOpen();
    return !windowEl.hidden;
}

export function setDiceWindowOpen(open: boolean): void {
    writeOpen(open);
    apply(open);
}

/**
 * Notified on every change, so a second control (the Settings tab) can stay in step. Returns an
 * unsubscribe so a caller that re-renders its control does not leave the old one in the set.
 */
export function subscribeDiceWindow(fn: (open: boolean) => void): () => void {
    listeners.add(fn);
    try { fn(isDiceWindowOpen()); } catch { /* presentation only */ }
    return () => { listeners.delete(fn); };
}

let mounted = false;

export function mountFt3dDiceWindow(root: HTMLElement): void {
    // Idempotent: a second call would bind a second set of listeners and re-run the portal against
    // a #sideLog that no longer holds the dice.
    if (mounted) return;
    windowEl = root.querySelector<HTMLElement>('[data-ft3d-dice-window]');
    // May be a rim tab in the route's own chrome rather than a HUD dock button.
    toggleEl = root.querySelector<HTMLElement>('[data-ft3d-dice-toggle]')
        ?? document.querySelector<HTMLElement>('[data-ft3d-dice-toggle]');
    if (!windowEl) return;

    const movedGroups = portalDiceControls(windowEl);
    const canonicalIds = ['raw_dice_mod', 'raw_dice_adv_dis_stacks', 'die_d20', 'btn_visble_roll']
        .filter((id) => document.getElementById(id) !== null);

    const receipt: Ft3dDiceWindowReceipt = {
        schema: 'ft.ft3d.dice-window.v1',
        portalIsQueryOnly: true,
        portalWrites: 0,
        movedGroups,
        canonicalIds,
    };
    (window as unknown as { __ft3dDiceWindow?: Ft3dDiceWindowReceipt }).__ft3dDiceWindow = receipt;

    if (toggleEl) {
        toggleEl.addEventListener('click', () => setDiceWindowOpen(!!windowEl?.hidden));
    }
    document.querySelectorAll<HTMLElement>('[data-ft3d-dice-close]').forEach((button) => {
        button.addEventListener('click', () => setDiceWindowOpen(false));
    });

    mounted = true;
    document.addEventListener('keydown', event => {
        const bindings = window as Window & { ftMatchFt3dKeyBinding?: (event: KeyboardEvent, action: string) => boolean };
        const matches = bindings.ftMatchFt3dKeyBinding ? bindings.ftMatchFt3dKeyBinding(event, 'dice')
            : event.key.toLowerCase() === 'v' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
        if (!matches || event.defaultPrevented || event.repeat
            || (event.target instanceof Element && event.target.closest('input,textarea,select,[contenteditable="true"],dialog,[role="dialog"]'))) return;
        event.preventDefault();
        setDiceWindowOpen(!isDiceWindowOpen());
    });
    apply(readOpen());
    window.addEventListener('ft:ft3d-keybindings-changed', () => apply(isDiceWindowOpen()));
    renderGlyph();
    window.setTimeout(renderGlyph, 0);
    window.addEventListener('load', renderGlyph, { once: true });
}
